import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, X, Check, ChefHat, ArrowRightLeft, PlusCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { SortableHeader, sortData } from "../components/SortableHeader";
import { useLand } from "../hooks/useLand";
import { useStandort } from "../hooks/useStandort";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KATEGORIEN = [
  "Vorspeise",
  "Hauptgericht",
  "Dessert",
  "Beilage",
  "Suppe",
  "Salat",
  "Snack"
];

// Einheiten-Umrechnung
const UNIT_CONVERSIONS = {
  kg: { to: 'g', factor: 1000, label: 'kg → g' },
  g: { to: 'kg', factor: 0.001, label: 'g → kg' },
  l: { to: 'ml', factor: 1000, label: 'l → ml' },
  ml: { to: 'l', factor: 0.001, label: 'ml → l' },
};

const convertUnit = (value, fromUnit) => {
  const conversion = UNIT_CONVERSIONS[fromUnit];
  if (!conversion) return null;
  return {
    value: value * conversion.factor,
    unit: conversion.to
  };
};

export const Speisen = () => {
  const { land, vat } = useLand();
  const { activeStandortId } = useStandort();
  const [speisen, setSpeisen] = useState([]);
  const [zutaten, setZutaten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSpeise, setEditingSpeise] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    kategorie: "Hauptgericht",
    verkaufspreis: "",
    portionsgroesse: "",
    beschreibung: "",
    zutaten: []
  });
  const [selectedZutat, setSelectedZutat] = useState("");
  const [zutatMenge, setZutatMenge] = useState("");
  const [zutatEinheit, setZutatEinheit] = useState("");
  
  // Neue Zutat Dialog
  const [neueZutatDialogOpen, setNeueZutatDialogOpen] = useState(false);
  const [neueZutatForm, setNeueZutatForm] = useState({
    name: "",
    einheit: "kg",
    preis_pro_einheit: "",
    lieferant: ""
  });

  const EINHEITEN = ["kg", "g", "l", "ml", "Stück", "Bund", "Packung"];
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStandortId]);

  const fetchData = async () => {
    try {
      const params = activeStandortId ? { standort_id: activeStandortId } : {};
      const [speisenRes, zutatenRes] = await Promise.all([
        axios.get(`${API}/speisen`, { params, withCredentials: true }),
        axios.get(`${API}/zutaten`, { withCredentials: true })
      ]);
      setSpeisen(speisenRes.data);
      setZutaten(zutatenRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      kategorie: "Hauptgericht",
      verkaufspreis: "",
      portionsgroesse: "",
      beschreibung: "",
      zutaten: []
    });
    setEditingSpeise(null);
    setSelectedZutat("");
    setZutatMenge("");
    setZutatEinheit("");
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (speise) => {
    setEditingSpeise(speise);
    setFormData({
      name: speise.name,
      kategorie: speise.kategorie,
      verkaufspreis: speise.verkaufspreis.toString(),
      portionsgroesse: speise.portionsgroesse || "",
      beschreibung: speise.beschreibung || "",
      zutaten: speise.zutaten || []
    });
    setDialogOpen(true);
  };

  // Wenn Zutat gewählt wird, setze die Standard-Einheit
  const handleZutatSelect = (zutatId) => {
    if (zutatId === "__new__") {
      setNeueZutatDialogOpen(true);
      return;
    }
    setSelectedZutat(zutatId);
    const zutat = zutaten.find(z => z.id === zutatId);
    if (zutat) {
      setZutatEinheit(zutat.einheit);
    }
  };

  // Neue Zutat erstellen
  const handleNeueZutatSubmit = async (e) => {
    e.preventDefault();
    if (!neueZutatForm.name || !neueZutatForm.preis_pro_einheit) {
      toast.error("Bitte Name und Preis angeben");
      return;
    }

    try {
      const response = await axios.post(`${API}/zutaten`, {
        name: neueZutatForm.name,
        einheit: neueZutatForm.einheit,
        preis_pro_einheit: parseFloat(neueZutatForm.preis_pro_einheit),
        lieferant: neueZutatForm.lieferant || null
      }, { withCredentials: true });

      toast.success("Zutat erstellt");
      setZutaten([...zutaten, response.data]);
      setNeueZutatDialogOpen(false);
      setNeueZutatForm({ name: "", einheit: "kg", preis_pro_einheit: "", lieferant: "" });
      
      // Automatisch die neue Zutat auswählen
      setSelectedZutat(response.data.id);
      setZutatEinheit(response.data.einheit);
    } catch (error) {
      toast.error("Fehler beim Erstellen");
    }
  };

  // Einheit umrechnen
  const handleUnitConversion = () => {
    if (!zutatMenge || !zutatEinheit) return;
    
    const converted = convertUnit(parseFloat(zutatMenge), zutatEinheit);
    if (converted) {
      setZutatMenge(converted.value.toString());
      setZutatEinheit(converted.unit);
      toast.success(`Umgerechnet: ${converted.value} ${converted.unit}`);
    } else {
      toast.error("Keine Umrechnung verfügbar für diese Einheit");
    }
  };

  // Berechne Kosten basierend auf der gewählten Einheit
  const calculateZutatKosten = (zutat, menge, einheit) => {
    const originalEinheit = zutat.einheit;
    let adjustedMenge = menge;
    
    if (einheit !== originalEinheit) {
      if ((originalEinheit === 'kg' && einheit === 'g') || 
          (originalEinheit === 'g' && einheit === 'kg')) {
        adjustedMenge = originalEinheit === 'kg' ? menge / 1000 : menge * 1000;
      }
      if ((originalEinheit === 'l' && einheit === 'ml') || 
          (originalEinheit === 'ml' && einheit === 'l')) {
        adjustedMenge = originalEinheit === 'l' ? menge / 1000 : menge * 1000;
      }
    }
    
    return adjustedMenge * zutat.preis_pro_einheit;
  };

  const addZutatToSpeise = () => {
    if (!selectedZutat || !zutatMenge) {
      toast.error("Bitte Zutat und Menge angeben");
      return;
    }

    const zutat = zutaten.find(z => z.id === selectedZutat);
    if (!zutat) return;

    const menge = parseFloat(zutatMenge);
    const kosten = calculateZutatKosten(zutat, menge, zutatEinheit);

    const newZutat = {
      zutat_id: zutat.id,
      zutat_name: zutat.name,
      menge: menge,
      einheit: zutatEinheit,
      kosten: Math.round(kosten * 100) / 100
    };

    setFormData({
      ...formData,
      zutaten: [...formData.zutaten, newZutat]
    });

    setSelectedZutat("");
    setZutatMenge("");
    setZutatEinheit("");
  };

  const removeZutatFromSpeise = (index) => {
    setFormData({
      ...formData,
      zutaten: formData.zutaten.filter((_, i) => i !== index)
    });
  };

  const calculateWareneinsatz = () => {
    return formData.zutaten.reduce((sum, z) => sum + z.kosten, 0);
  };

  const calculateMarge = () => {
    const verkauf = parseFloat(formData.verkaufspreis) || 0;
    const einsatz = calculateWareneinsatz();
    return verkauf - einsatz;
  };

  // MwSt-Berechnung fuer Speisen (dynamisch nach Land: DE 7% / AT 10%)
  const MWST_SPEISEN = vat.speisen;
  
  const calculateNettoPreis = () => {
    const brutto = parseFloat(formData.verkaufspreis) || 0;
    return brutto / (1 + MWST_SPEISEN / 100);
  };

  const calculateMwStBetrag = () => {
    const brutto = parseFloat(formData.verkaufspreis) || 0;
    const netto = calculateNettoPreis();
    return brutto - netto;
  };

  const calculateMargeNetto = () => {
    const netto = calculateNettoPreis();
    const einsatz = calculateWareneinsatz();
    return netto - einsatz;
  };

  const calculateMargeProzent = () => {
    const netto = calculateNettoPreis();
    if (netto <= 0) return 0;
    const einsatz = calculateWareneinsatz();
    return ((netto - einsatz) / netto) * 100;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.verkaufspreis) {
      toast.error("Bitte Name und Verkaufspreis angeben");
      return;
    }

    try {
      const payload = {
        ...formData,
        verkaufspreis: parseFloat(formData.verkaufspreis),
        // Bei neuem Eintrag aktiven Standort setzen (oder bestehenden behalten)
        standort_id: formData.standort_id !== undefined
          ? formData.standort_id
          : (editingSpeise ? editingSpeise.standort_id : activeStandortId) || null,
      };

      if (editingSpeise) {
        await axios.put(`${API}/speisen/${editingSpeise.id}`, payload, { withCredentials: true });
        toast.success("Speise aktualisiert");
      } else {
        await axios.post(`${API}/speisen`, payload, { withCredentials: true });
        toast.success("Speise erstellt");
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Speise wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/speisen/${id}`, { withCredentials: true });
      toast.success("Speise gelöscht");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-[#858585]">Laden...</div>
      </div>
    );
  }

  return (
    <div data-testid="speisen-page">
      {/* Header */}
      <header className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Speisen</h1>
            <p className="page-subtitle">Verwalten Sie Ihre Gerichte und Rezepturen</p>
          </div>
          <Button 
            onClick={openCreateDialog}
            className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm uppercase text-xs tracking-wide btn-press"
            data-testid="add-speise-btn"
          >
            <Plus size={16} className="mr-2" />
            Neue Speise
          </Button>
        </div>
      </header>

      {/* Content */}
      {speisen.length === 0 ? (
        <div className="empty-state">
          <ChefHat size={64} className="mx-auto text-[#D1CFC9]" />
          <h3>Keine Speisen vorhanden</h3>
          <p>Erstellen Sie Ihre erste Speise mit Zutaten-Kalkulation</p>
          <Button 
            onClick={openCreateDialog}
            className="mt-4 bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm"
            data-testid="add-speise-empty-btn"
          >
            <Plus size={16} className="mr-2" />
            Erste Speise erstellen
          </Button>
        </div>
      ) : (
        <div className="data-table-container" data-testid="speisen-table">
          <table className="data-table">
            <thead>
              <tr>
                <SortableHeader label="Name" sortKey="name" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Kategorie" sortKey="kategorie" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Wareneinsatz" sortKey="wareneinsatz" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Verkaufspreis" sortKey="verkaufspreis" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Marge" sortKey="marge" sortConfig={sortConfig} onSort={setSortConfig} />
                <SortableHeader label="Marge %" sortKey="marge_prozent" sortConfig={sortConfig} onSort={setSortConfig} />
                <th className="text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {sortData(speisen, sortConfig).map((speise) => (
                <tr key={speise.id} data-testid={`speise-row-${speise.id}`}>
                  <td className="font-body font-medium">{speise.name}</td>
                  <td>
                    <span className="badge badge-neutral">{speise.kategorie}</span>
                  </td>
                  <td>{formatCurrency(speise.wareneinsatz)}</td>
                  <td>{formatCurrency(speise.verkaufspreis)}</td>
                  <td className={speise.marge >= 0 ? 'text-profit' : 'text-loss'}>
                    {formatCurrency(speise.marge)}
                  </td>
                  <td>
                    <span className={`badge ${speise.marge_prozent >= 50 ? 'badge-profit' : speise.marge_prozent >= 30 ? 'badge-neutral' : 'badge-loss'}`}>
                      {speise.marge_prozent}%
                    </span>
                  </td>
                  <td className="text-right">
                    <div className="flex justify-end gap-2">
                      <button 
                        className="btn-icon"
                        onClick={() => openEditDialog(speise)}
                        data-testid={`edit-speise-${speise.id}`}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button 
                        className="btn-icon danger"
                        onClick={() => handleDelete(speise.id)}
                        data-testid={`delete-speise-${speise.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editingSpeise ? "Speise bearbeiten" : "Neue Speise erstellen"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column - Basic Info */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="z.B. Wiener Schnitzel"
                    className="border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#3A4D39] font-mono"
                    data-testid="speise-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Kategorie</Label>
                  <Select 
                    value={formData.kategorie} 
                    onValueChange={(value) => setFormData({...formData, kategorie: value})}
                  >
                    <SelectTrigger className="border-0 border-b border-stone-300 rounded-none px-0 focus:ring-0" data-testid="speise-kategorie-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {KATEGORIEN.map((kat) => (
                        <SelectItem key={kat} value={kat}>{kat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Verkaufspreis (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.verkaufspreis}
                    onChange={(e) => setFormData({...formData, verkaufspreis: e.target.value})}
                    placeholder="0.00"
                    className="border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#3A4D39] font-mono"
                    data-testid="speise-preis-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Portionsgröße</Label>
                  <Input
                    value={formData.portionsgroesse}
                    onChange={(e) => setFormData({...formData, portionsgroesse: e.target.value})}
                    placeholder="z.B. 350g"
                    className="border-0 border-b border-stone-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#3A4D39] font-mono"
                    data-testid="speise-portion-input"
                  />
                </div>
              </div>

              {/* Right Column - Zutaten */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Zutaten hinzufügen</Label>
                  <div className="flex gap-2">
                    <Select value={selectedZutat} onValueChange={handleZutatSelect}>
                      <SelectTrigger className="flex-1 border-stone-300 rounded-sm" data-testid="zutat-select">
                        <SelectValue placeholder="Zutat wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__new__" className="text-[#3A4D39] font-medium">
                          <div className="flex items-center gap-2">
                            <PlusCircle size={14} />
                            Neue Zutat erstellen
                          </div>
                        </SelectItem>
                        <div className="border-t border-stone-200 my-1" />
                        {zutaten.map((z) => (
                          <SelectItem key={z.id} value={z.id}>
                            {z.name} ({formatCurrency(z.preis_pro_einheit)}/{z.einheit})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Menge und Einheit mit Umrechnung */}
                  {selectedZutat && (
                    <div className="flex gap-2 items-end mt-2">
                      <div className="flex-1">
                        <Label className="text-[10px] uppercase text-[#858585]">Menge</Label>
                        <Input
                          type="number"
                          step="0.001"
                          value={zutatMenge}
                          onChange={(e) => setZutatMenge(e.target.value)}
                          placeholder="0.00"
                          className="border-stone-300 rounded-sm font-mono"
                          data-testid="zutat-menge-input"
                        />
                      </div>
                      <div className="w-20">
                        <Label className="text-[10px] uppercase text-[#858585]">Einheit</Label>
                        <Select value={zutatEinheit} onValueChange={setZutatEinheit}>
                          <SelectTrigger className="border-stone-300 rounded-sm" data-testid="zutat-einheit-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {(() => {
                              const zutat = zutaten.find(z => z.id === selectedZutat);
                              if (!zutat) return null;
                              const baseUnit = zutat.einheit;
                              const conversion = UNIT_CONVERSIONS[baseUnit];
                              const units = [baseUnit];
                              if (conversion) units.push(conversion.to);
                              return units.map(u => (
                                <SelectItem key={u} value={u}>{u}</SelectItem>
                              ));
                            })()}
                          </SelectContent>
                        </Select>
                      </div>
                      {UNIT_CONVERSIONS[zutatEinheit] && (
                        <Button 
                          type="button"
                          variant="outline"
                          onClick={handleUnitConversion}
                          className="rounded-sm px-2"
                          title={UNIT_CONVERSIONS[zutatEinheit]?.label}
                          data-testid="convert-unit-btn"
                        >
                          <ArrowRightLeft size={16} />
                        </Button>
                      )}
                      <Button 
                        type="button"
                        onClick={addZutatToSpeise}
                        className="bg-[#3A4D39] hover:bg-[#2C3B2B] rounded-sm"
                        data-testid="add-zutat-btn"
                      >
                        <Plus size={16} />
                      </Button>
                    </div>
                  )}
                  
                  {/* Hinweis zur Umrechnung */}
                  {selectedZutat && UNIT_CONVERSIONS[zutatEinheit] && (
                    <p className="text-[10px] text-[#858585] mt-1">
                      Tipp: Klicken Sie ↔ um zwischen {zutatEinheit} und {UNIT_CONVERSIONS[zutatEinheit]?.to} umzurechnen
                    </p>
                  )}
                </div>

                {/* Zutaten List */}
                <div className="border border-stone-200 divide-y divide-stone-100 max-h-48 overflow-y-auto">
                  {formData.zutaten.length === 0 ? (
                    <div className="p-4 text-center text-[#858585] text-sm">
                      Keine Zutaten hinzugefügt
                    </div>
                  ) : (
                    formData.zutaten.map((z, idx) => {
                      // Finde Original-Zutat für Preis-Neuberechnung
                      const originalZutat = zutaten.find(oz => oz.id === z.zutat_id);
                      
                      const updateZutatMenge = (newMenge) => {
                        const menge = parseFloat(newMenge) || 0;
                        const kosten = originalZutat 
                          ? calculateZutatKosten(originalZutat, menge, z.einheit)
                          : z.kosten;
                        
                        const updatedZutaten = [...formData.zutaten];
                        updatedZutaten[idx] = {
                          ...z,
                          menge: menge,
                          kosten: Math.round(kosten * 100) / 100
                        };
                        setFormData({ ...formData, zutaten: updatedZutaten });
                      };
                      
                      return (
                        <div key={z.zutat_id || `zutat-${idx}`} className="flex items-center justify-between p-2 text-sm" data-testid={`zutat-item-${idx}`}>
                          <div className="flex items-center gap-2 flex-1">
                            <span className="font-medium min-w-[100px]">{z.zutat_name}</span>
                            <Input
                              type="number"
                              step="0.001"
                              value={z.menge}
                              onChange={(e) => updateZutatMenge(e.target.value)}
                              className="w-20 h-7 text-xs font-mono border-stone-300 px-2"
                              data-testid={`edit-menge-${idx}`}
                            />
                            <span className="text-[#858585] text-xs">{z.einheit}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-green-600">{formatCurrency(z.kosten)}</span>
                            <button
                              type="button"
                              onClick={() => removeZutatFromSpeise(idx)}
                              className="text-[#C62828] hover:bg-red-50 p-1"
                              data-testid={`remove-zutat-${idx}`}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Receipt Summary */}
                <div className="receipt-summary">
                  <div className="receipt-header">
                    <p className="receipt-title">Kalkulation</p>
                    <span className="text-xs text-slate-400">{land === "AT" ? "USt" : "MwSt"}: {vat.speisen_label}</span>
                  </div>
                  <div className="receipt-line">
                    <span>Wareneinsatz:</span>
                    <span>{formatCurrency(calculateWareneinsatz())}</span>
                  </div>
                  <div className="receipt-line">
                    <span>VK (brutto):</span>
                    <span>{formatCurrency(parseFloat(formData.verkaufspreis) || 0)}</span>
                  </div>
                  <div className="receipt-line text-xs text-slate-400">
                    <span>VK (netto):</span>
                    <span>{formatCurrency(calculateNettoPreis())}</span>
                  </div>
                  <div className="receipt-line text-xs text-slate-400">
                    <span>{land === "AT" ? "USt" : "MwSt"} ({MWST_SPEISEN}%):</span>
                    <span>{formatCurrency(calculateMwStBetrag())}</span>
                  </div>
                  <div className="receipt-divider" />
                  <div className="receipt-total">
                    <span>Marge (netto):</span>
                    <span className={calculateMargeNetto() >= 0 ? 'text-profit' : 'text-loss'}>
                      {formatCurrency(calculateMargeNetto())} ({calculateMargeProzent().toFixed(1)}%)
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-stone-100">
              <Button 
                type="button" 
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="rounded-sm"
                data-testid="cancel-speise-btn"
              >
                Abbrechen
              </Button>
              <Button 
                type="submit"
                className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm btn-press"
                data-testid="save-speise-btn"
              >
                <Check size={16} className="mr-2" />
                {editingSpeise ? "Aktualisieren" : "Erstellen"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Neue Zutat Dialog */}
      <Dialog open={neueZutatDialogOpen} onOpenChange={setNeueZutatDialogOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Neue Zutat erstellen</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleNeueZutatSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Name *</Label>
              <Input
                value={neueZutatForm.name}
                onChange={(e) => setNeueZutatForm({...neueZutatForm, name: e.target.value})}
                placeholder="z.B. Kartoffeln"
                className="border-stone-300 rounded-sm"
                data-testid="neue-zutat-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Einheit</Label>
                <Select value={neueZutatForm.einheit} onValueChange={(v) => setNeueZutatForm({...neueZutatForm, einheit: v})}>
                  <SelectTrigger className="border-stone-300 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EINHEITEN.map((e) => (
                      <SelectItem key={e} value={e}>{e}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Preis pro {neueZutatForm.einheit} *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={neueZutatForm.preis_pro_einheit}
                  onChange={(e) => setNeueZutatForm({...neueZutatForm, preis_pro_einheit: e.target.value})}
                  placeholder="0.00"
                  className="border-stone-300 rounded-sm font-mono"
                  data-testid="neue-zutat-preis"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Lieferant (optional)</Label>
              <Input
                value={neueZutatForm.lieferant}
                onChange={(e) => setNeueZutatForm({...neueZutatForm, lieferant: e.target.value})}
                placeholder="z.B. Metro"
                className="border-stone-300 rounded-sm"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-stone-100">
              <Button type="button" variant="outline" onClick={() => setNeueZutatDialogOpen(false)} className="rounded-sm">
                Abbrechen
              </Button>
              <Button type="submit" className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm">
                <Check size={16} className="mr-2" />
                Zutat erstellen
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Speisen;
